import { useQuery, useMutation } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Hook for fetching quiz configuration data
export const useQuizConfiguration = (options = {}) => {
  return useQuery({
    queryKey: ['quiz-config'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/api/quiz/config`);
      if (!response.ok) {
        throw new Error('Failed to fetch quiz configuration');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

// Hook for fetching songs with questions
export const useSongsWithQuestions = (difficultyId, topicId, options = {}) => {
  return useQuery({
    queryKey: ['songs-with-questions', difficultyId, topicId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (difficultyId) params.append('difficulty_id', difficultyId);
      if (topicId) params.append('topic_id', topicId);
      
      const response = await fetch(`${API_BASE}/api/quiz/songs-with-questions?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch songs with questions');
      }
      return response.json();
    },
    enabled: !!difficultyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

// Hook for generating quiz questions
export const useGenerateQuiz = (options = {}) => {
  return useMutation({
    mutationFn: async (config) => {
      const response = await fetch(`${API_BASE}/api/quiz/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate quiz');
      }
      
      return response.json();
    },
    ...options,
  });
};

// Hook for submitting quiz results (optional)
export const useSubmitQuizResult = (options = {}) => {
  return useMutation({
    mutationFn: async (resultData) => {
      const response = await fetch(`${API_BASE}/api/quiz/submit-result`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resultData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit quiz result');
      }
      
      return response.json();
    },
    ...options,
  });
};
